import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const APP_NAME = 'Badminton Leagues'
const DEFAULT_TITLE = `${APP_NAME} | Group Rankings & Match Tracking`
const DEFAULT_DESCRIPTION = 'Free badminton league system for groups. Track rankings, record match scores, manage seasons, and compete with friends. Perfect for social badminton groups.'
const DEFAULT_IMAGE = 'https://badminton-leagues.com/og-image.svg'
const SITE_URL = 'https://badminton-leagues.com'

interface DocumentHeadOptions {
  /** Page title (will be appended with app name) */
  title?: string
  /** Full title override (won't append app name) */
  fullTitle?: string
  /** Page description for meta tags */
  description?: string
  /** Open Graph image URL */
  image?: string
  /** Page type for Open Graph (default: website) */
  type?: 'website' | 'article' | 'profile'
  /** Override canonical URL (defaults to current path) */
  canonicalPath?: string
  /** Prevent search engines from indexing this page */
  noIndex?: boolean
}

// Pages that should not be indexed (user-specific content)
const NO_INDEX_PATHS = ['/profile', '/profile-setup', '/notifications', '/matches', '/leagues', '/groups', '/invite']

/**
 * Hook to manage document head (title and meta tags)
 * Updates on every render, so memoize options if they don't change
 */
export function useDocumentHead(options: DocumentHeadOptions = {}) {
  const location = useLocation()
  
  const {
    title,
    fullTitle,
    description = DEFAULT_DESCRIPTION,
    image,
    type = 'website',
    canonicalPath,
    noIndex,
  } = options

  useEffect(() => {
    // Set document title
    const pageTitle = fullTitle ?? (title ? `${title} | ${APP_NAME}` : DEFAULT_TITLE)
    document.title = pageTitle

    // Determine canonical URL
    const currentPath = canonicalPath ?? location.pathname
    const canonicalUrl = `${SITE_URL}${currentPath}`
    
    // Determine if page should be indexed
    const shouldNoIndex = noIndex ?? (NO_INDEX_PATHS.some(p => currentPath.startsWith(p)) || currentPath.includes('/admin'))

    // Update meta tags
    updateMetaTag('description', description)
    updateMetaTag('robots', shouldNoIndex ? 'noindex, nofollow' : 'index, follow')
    
    // Open Graph
    updateMetaTag('og:title', pageTitle)
    updateMetaTag('og:description', description)
    updateMetaTag('og:type', type)
    updateMetaTag('og:url', canonicalUrl)
    updateMetaTag('og:image', image || DEFAULT_IMAGE)
    updateMetaTag('og:site_name', APP_NAME)
    
    // Twitter
    updateMetaTag('twitter:card', image ? 'summary_large_image' : 'summary')
    updateMetaTag('twitter:title', pageTitle)
    updateMetaTag('twitter:description', description)
    updateMetaTag('twitter:image', image || DEFAULT_IMAGE)
    
    // Canonical
    updateLinkTag('canonical', canonicalUrl)

    // Cleanup - restore defaults on unmount
    return () => {
      document.title = DEFAULT_TITLE
      updateMetaTag('robots', 'index, follow')
    }
  }, [title, fullTitle, description, image, type, canonicalPath, noIndex, location.pathname])
}

function updateMetaTag(name: string, content: string) {
  const isOg = name.startsWith('og:')
  const isTwitter = name.startsWith('twitter:')
  
  let selector: string
  if (isOg || isTwitter) {
    selector = `meta[property="${name}"]`
  } else {
    selector = `meta[name="${name}"]`
  }
  
  let tag = document.querySelector<HTMLMetaElement>(selector)
  
  if (!tag) {
    tag = document.createElement('meta')
    if (isOg || isTwitter) {
      tag.setAttribute('property', name)
    } else {
      tag.setAttribute('name', name)
    }
    document.head.appendChild(tag)
  }
  
  tag.setAttribute('content', content)
}

function updateLinkTag(rel: string, href: string) {
  let tag = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  
  if (!tag) {
    tag = document.createElement('link')
    tag.setAttribute('rel', rel)
    document.head.appendChild(tag)
  }
  
  tag.setAttribute('href', href)
}

/**
 * Convenience hook for just setting page title
 */
export function usePageTitle(title?: string) {
  useDocumentHead({ title })
}

export { APP_NAME, DEFAULT_TITLE, DEFAULT_DESCRIPTION, SITE_URL }
